import { readmeDocsUrl } from 'App/utils';
import { appTokenStorageKey } from 'App/components/utils/providers/AppUserProvider';

const defaultDocsPath = '/reference';

// A path on the docs site only: no host, no "//", no backslash, no whitespace.
const docsPathPattern = /^\/(?![/\\])[^\s\\]*$/;

export const safeDocsPath = ( path: string ): string => {
  return docsPathPattern.test( path ) ? path : defaultDocsPath;
};

export const docsPathFromRedirect = ( redirect: string | null ): string => {
  if ( !redirect ) {
    return defaultDocsPath;
  }
  try {
    return safeDocsPath( decodeURIComponent( redirect ) );
  } catch {
    return defaultDocsPath;
  }
};

// EIS's docs sign-in page: a link opened in a new tab skips the click, so this page signs in instead.
export const docsSignInPath = ( path: string ): string => `/login/readme?redirect=${encodeURIComponent( path )}`;

// ReadMe signs the user in from a form POST, so the docs token never shows in a URL.
const submitDocsForm = ( doc: Document, target: string, docsToken: string, path: string ): void => {
  const form = doc.createElement( 'form' );
  form.method = 'POST';
  form.action = `${readmeDocsUrl}/login`;
  form.target = target;
  form.setAttribute( 'rel', 'noopener noreferrer' );

  const fields: Record<string, string> = { auth_token: docsToken, redirect: safeDocsPath( path ) };
  Object.keys( fields ).forEach( ( name ) => {
    const input = doc.createElement( 'input' );
    input.type = 'hidden';
    input.name = name;
    input.value = fields[name];
    form.appendChild( input );
  } );

  doc.body.appendChild( form );
  form.submit();
  form.remove();
};

export const openReadmeDocs = ( docsToken: string, path: string ): void => {
  submitDocsForm( document, '_self', docsToken, path );
};

// Opened inside the click: a tab opened after an await is blocked as a popup (Safari, Firefox).
export const openDocsTab = (): Window | null => {
  const tab = window.open( '', '_blank' );
  if ( tab ) {
    tab.opener = null;
  }
  return tab;
};

// Posts inside the tab from openDocsTab, or into a new tab if the browser refused one.
export const openReadmeDocsIn = ( tab: Window | null, docsToken: string, path: string ): void => {
  submitDocsForm( tab ? tab.document : document, tab ? '_self' : '_blank', docsToken, path );
};

// Older versions saved a docs token that never expires in APP_TOKEN.
export const removeStoredDocsToken = (): void => {
  const appTokens = JSON.parse( localStorage.getItem( appTokenStorageKey ) || 'null' );
  if ( appTokens && 'readmeToken' in appTokens ) {
    delete appTokens.readmeToken;
    localStorage.setItem( appTokenStorageKey, JSON.stringify( appTokens ) );
  }
};
